/**
 * Шкала времени просмотрщика камер: обзорная полоса 00–24 с рамкой видимого окна и главная шкала с
 * зумом от 2 минут до суток. Колесо — зум вокруг точки под мышью, Shift+колесо и перетаскивание —
 * сдвиг, клик — курсор, ручки отрезка «от–до» тянутся мышью, треугольники снизу — события сделки.
 * Зелёным — где Macroscop писал (запись по движению). Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { type Fragment, clock, dayOf, secOf, videoFragmentsQuery } from "@/lib/video";

export type Win = { start: number; span: number };
export type Mark = { at: number; title: string };

export const DAY = 86400;
export const MIN_SPAN = 120;
export const DAY_WIN: Win = { start: 8 * 3600, span: 15 * 3600 };
const TICKS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200];
const PRESETS: [string, number][] = [
  ["2 ч", 7200],
  ["30 мин", 1800],
  ["5 мин", 300],
];
const zbtn = "h-7 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px] hover:bg-bg-subtle";

export const clampWin = (w: Win): Win => {
  const span = Math.max(MIN_SPAN, Math.min(DAY, w.span));
  return { span, start: Math.max(0, Math.min(DAY - span, w.start)) };
};
export const around = (t: number, span: number) => clampWin({ start: t - span / 2, span });

/** Отрезки записи дня в секундах от полуночи (перешедшие через полночь обрезаны). */
export function spans(fragments: Fragment[] | undefined, day: string): [number, number][] {
  return (fragments ?? []).map((f) => [dayOf(f.from) < day ? 0 : secOf(f.from), dayOf(f.to) > day ? DAY : secOf(f.to)]);
}

const label = (t: number, step: number) => (step >= 60 ? clock(t).slice(0, 5) : clock(t));

export function Timeline({
  camera,
  day,
  cur,
  from,
  to,
  win,
  marks,
  onWin,
  onPick,
  onRange,
}: {
  camera: number;
  day: string;
  cur: number;
  from: number | null;
  to: number | null;
  win: Win;
  marks: Mark[];
  onWin: (w: Win) => void;
  onPick: (s: number) => void;
  onRange: (from: number | null, to: number | null) => void;
}) {
  const q = useQuery(videoFragmentsQuery(camera, day));
  const rec = spans(q.data?.fragments, day);
  const scale = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const [hover, setHover] = useState<number | null>(null);
  // Колесо слушаем сами: у React onWheel пассивный и прокрутку страницы не отменить.
  const latest = useRef({ win, onWin });
  latest.current = { win, onWin };
  useEffect(() => {
    const el = scale.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 1000));
    ro.observe(el);
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const { win: w, onWin: set } = latest.current;
      const r = el.getBoundingClientRect();
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const d = e.shiftKey ? e.deltaY || e.deltaX : e.deltaX;
        set(clampWin({ ...w, start: w.start + (d / r.width) * w.span }));
        return;
      }
      const t = w.start + ((e.clientX - r.left) / r.width) * w.span;
      const span = Math.max(MIN_SPAN, Math.min(DAY, w.span * (e.deltaY > 0 ? 1.25 : 0.8)));
      set(clampWin({ span, start: t - ((t - w.start) * span) / w.span }));
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => (ro.disconnect(), el.removeEventListener("wheel", wheel));
  }, []);

  const x = (t: number) => ((t - win.start) / win.span) * 100;
  const pct = (t: number) => `${Math.max(-1, Math.min(101, x(t)))}%`;
  const wpct = (a: number, b: number) => `${Math.max(0, x(Math.min(b, win.start + win.span)) - x(Math.max(a, win.start)))}%`;
  const tAt = (clientX: number) => {
    const r = scale.current!.getBoundingClientRect();
    return win.start + ((clientX - r.left) / r.width) * win.span;
  };
  const step = TICKS.find((s) => win.span / s <= 12) ?? 7200;
  const minor = (step / 5 / win.span) * width >= 6 ? step / 5 : null;
  const ticks: number[] = [];
  for (let t = Math.ceil(win.start / (minor ?? step)) * (minor ?? step); t <= win.start + win.span; t += minor ?? step) ticks.push(t);

  // Перетаскивание: по ручке — край отрезка, иначе — сдвиг окна; без сдвига мыши — клик.
  const drag = useRef<{ x0: number; start: number; mode: "pan" | "from" | "to"; moved: boolean } | null>(null);
  const near = (clientX: number, t: number | null) => {
    if (t == null || !scale.current) return false;
    const r = scale.current.getBoundingClientRect();
    return Math.abs(r.left + (x(t) / 100) * r.width - clientX) <= 8;
  };
  const down = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const mode = near(e.clientX, from) ? "from" : near(e.clientX, to) ? "to" : "pan";
    drag.current = { x0: e.clientX, start: win.start, mode, moved: false };
  };
  const move = (e: React.PointerEvent) => {
    setHover(tAt(e.clientX));
    const d = drag.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.x0) >= 4) d.moved = true;
    if (!d.moved) return;
    const t = Math.round(Math.max(0, Math.min(DAY - 1, tAt(e.clientX))));
    if (d.mode === "from") onRange(Math.min(t, (to ?? DAY) - 3), to);
    else if (d.mode === "to") onRange(from, Math.max(t, (from ?? 0) + 3));
    else onWin(clampWin({ ...win, start: d.start - ((e.clientX - d.x0) / scale.current!.clientWidth) * win.span }));
  };
  const up = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (d && !d.moved) onPick(Math.round(tAt(e.clientX)));
  };

  // Обзор: клик и перетаскивание ставят центр окна.
  const overview = useRef<HTMLDivElement>(null);
  const centerAt = (clientX: number) => {
    const r = overview.current!.getBoundingClientRect();
    onWin(around(((clientX - r.left) / r.width) * DAY, win.span));
  };
  const gap = rec.some(([a, b]) => cur >= a && cur < b) ? null : gapAround(rec, cur);
  const curOut = cur < win.start ? "left" : cur > win.start + win.span ? "right" : null;
  const nearest = marks.length ? marks.reduce((a, b) => (Math.abs(b.at - cur) < Math.abs(a.at - cur) ? b : a)) : null;

  return (
    <div className="flex select-none flex-col gap-1" data-annot="полоса записи">
      <div
        ref={overview}
        className="relative h-2.5 cursor-pointer overflow-hidden rounded-sm bg-bg-subtle"
        title="Обзор суток: клик или перетаскивание — показать это время"
        onPointerDown={(e) => (e.currentTarget.setPointerCapture(e.pointerId), centerAt(e.clientX))}
        onPointerMove={(e) => e.buttons === 1 && centerAt(e.clientX)}
      >
        {rec.map(([a, b]) => (
          <div key={a} className="absolute inset-y-0 bg-ok/40" style={{ left: `${(a / DAY) * 100}%`, width: `${((b - a) / DAY) * 100}%` }} />
        ))}
        <div className="absolute inset-y-0 rounded-sm border border-accent bg-accent/10" style={{ left: `${(win.start / DAY) * 100}%`, width: `${Math.max(0.4, (win.span / DAY) * 100)}%` }} />
        <div className="absolute inset-y-0 w-0.5 bg-err" style={{ left: `${(cur / DAY) * 100}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-fg-muted tabular-nums">
        {[0, 4, 8, 12, 16, 20, 24].map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>

      <div className="relative h-4 text-[10.5px] text-fg-muted tabular-nums">
        {ticks
          .filter((t) => t % step === 0)
          .map((t) => (
            <span key={t} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: pct(t) }}>
              {label(t, step)}
            </span>
          ))}
      </div>
      <div
        ref={scale}
        className="relative h-8 cursor-pointer overflow-hidden rounded bg-bg-subtle active:cursor-grabbing"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={() => setHover(null)}
        title="Клик — кадр на это время · колесо — приблизить · перетащить — сдвинуть"
      >
        {ticks.map((t) => (
          <div key={t} className={`absolute bottom-0 w-px ${t % step === 0 ? "h-full bg-border" : "h-1.5 bg-border"}`} style={{ left: pct(t) }} />
        ))}
        {rec.map(([a, b]) =>
          b > win.start && a < win.start + win.span ? <div key={a} className="absolute inset-y-1.5 bg-ok/45" style={{ left: pct(Math.max(a, win.start)), width: wpct(a, b) }} /> : null,
        )}
        {from != null && to != null && to > from && (
          <div className="absolute inset-y-0 bg-accent/20" style={{ left: pct(Math.max(from, win.start)), width: wpct(from, to) }} />
        )}
        {[from, to].map((t, i) =>
          t != null && t >= win.start && t <= win.start + win.span ? (
            <div key={i} className="absolute inset-y-0 w-2 -translate-x-1/2 cursor-ew-resize" style={{ left: pct(t) }} title={`${i ? "Конец" : "Начало"} ${clock(t)} — тянуть мышью`}>
              <div className="mx-auto h-full w-0.5 bg-accent" />
            </div>
          ) : null,
        )}
        {hover != null && <div className="pointer-events-none absolute inset-y-0 w-px bg-fg/30" style={{ left: pct(hover) }} />}
        {!curOut && <div className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-err" style={{ left: pct(cur) }} />}
        {curOut && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onWin(around(cur, win.span))}
            className={`absolute top-1 rounded bg-err px-1 text-[10.5px] text-white tabular-nums ${curOut === "left" ? "left-1" : "right-1"}`}
          >
            {curOut === "left" ? "◂ " : ""}
            {clock(cur)}
            {curOut === "right" ? " ▸" : ""}
          </button>
        )}
        {hover != null && (
          <span className="pointer-events-none absolute top-0 -translate-x-1/2 rounded bg-fg/75 px-1 text-[10px] text-bg tabular-nums" style={{ left: pct(hover) }}>
            {clock(hover)}
          </span>
        )}
      </div>
      <div className="relative h-2.5">
        {marks
          .filter((m) => m.at >= win.start && m.at <= win.start + win.span)
          .map((m) => (
            <button
              key={`${m.at}-${m.title}`}
              type="button"
              onClick={() => onPick(m.at)}
              title={`${m.title} · ${clock(m.at)}`}
              className="absolute top-0 size-0 -translate-x-1/2 border-x-[6px] border-b-[9px] border-x-transparent border-b-accent hover:border-b-err"
              style={{ left: pct(m.at) }}
            />
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-1 text-[12px]">
        <button type="button" className={zbtn} onClick={() => onWin(around(cur, win.span * 1.6))} title="Отдалить (−)">
          −
        </button>
        <button type="button" className={zbtn} onClick={() => onWin(around(cur, win.span / 1.6))} title="Приблизить (+)">
          +
        </button>
        <button type="button" className={zbtn} onClick={() => onWin(DAY_WIN)} title="Весь рабочий день (0)">
          День
        </button>
        {PRESETS.map(([name, span]) => (
          <button key={span} type="button" className={`${zbtn} ${Math.abs(win.span - span) < 1 ? "border-accent text-accent-text" : ""}`} onClick={() => onWin(around(cur, span))}>
            {name}
          </button>
        ))}
        {nearest && (
          <button type="button" className={zbtn} onClick={() => (onPick(nearest.at), onWin(around(nearest.at, 1800)))} title={nearest.title}>
            ⌖ к событию
          </button>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-1 text-fg-muted tabular-nums">
          {gap ? (
            <>
              <span>
                нет записи {gap[0] != null ? clock(gap[0]) : "…"}–{gap[1] != null ? clock(gap[1]) : "…"}
              </span>
              {gap[0] != null && (
                <button type="button" className={zbtn} onClick={() => onPick(Math.max(0, gap[0]! - 1))}>
                  ← {clock(gap[0])}
                </button>
              )}
              {gap[1] != null && (
                <button type="button" className={zbtn} onClick={() => onPick(gap[1]!)}>
                  {clock(gap[1])} →
                </button>
              )}
            </>
          ) : (
            <span>курсор {clock(cur)}</span>
          )}
        </span>
      </div>
      {q.isError && (
        <div className="text-[11.5px] text-err">
          Отрезки записи не загрузились: {q.error.message}{" "}
          <button type="button" className="underline" onClick={() => q.refetch()}>
            Повторить
          </button>
        </div>
      )}
      {q.data && rec.length === 0 && <div className="text-[11.5px] text-fg-muted">За этот день записи нет.</div>}
    </div>
  );
}

/** Пауза записи вокруг момента: конец предыдущего отрезка и начало следующего. */
function gapAround(rec: [number, number][], t: number): [number | null, number | null] | null {
  if (!rec.length) return null;
  const before = rec.filter(([, b]) => b <= t).map(([, b]) => b);
  const after = rec.filter(([a]) => a > t).map(([a]) => a);
  return [before.length ? Math.max(...before) : null, after.length ? Math.min(...after) : null];
}
